import { ActorSubclass, HttpAgent } from '@icp-sdk/core/agent'
import { ODIN_BACKEND_CANISTER } from '../constants/canisterIds'
import { UNKNOWN_ERROR, UNSUPPORTED_TOKEN } from '../constants/swapErrors'
import { ODINBTC_ID } from '../constants/tokenIds'
import { _SERVICE as OdinActor, idlFactory as odinActor } from '../idls/odinBackend/odinBackend.did'
import {
  _SERVICE as OdinTradeActor,
  idlFactory as odinTradeActor,
  TradeAmount,
  TradeType,
} from '../idls/odinTrade/odinTrade.did'
import { OdinToken } from '../interfaces/common'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
} from '../interfaces/swapInterface'
import { createActor } from '../utils/createActor'

const ODIN_API_URL = 'https://api.odin.fun/v1'
const ODIN_TRADE_CANISTER = 'z2vm5-gaaaa-aaaaj-azw6q-cai'

export const MIN_ODIN_SWAP_AMOUNT = 0.000005

type TokenInfo = {
  id: string
  name: string
  description: string
  image: string
  creator: string
  created_time: string
  volume: number
  bonded: boolean
  icrc_ledger: string
  price: number
  marketcap: number
  rune: string
  featured: boolean
  holder_count: number
  holder_top: number
  holder_dev: number
  comment_count: number
  sold: number
  twitter: string
  website: string
  telegram: string
  last_comment_time: string
  sell_count: number
  buy_count: number
  ticker: string
  btc_liquidity: number
  token_liquidity: number
  user_btc_liquidity: number
  user_token_liquidity: number
  user_lp_tokens: number
  total_supply: number
  swap_fees: number
  swap_fees_24: number
  swap_volume: number
  swap_volume_24: number
  threshold: number
  txn_count: number
  divisibility: number
  decimals: number
  withdrawals: boolean
  deposits: boolean
  trading: boolean
  external: boolean
  price_5m: number
  price_1h: number
  price_6h: number
  price_1d: number
  rune_id: string
  last_action_time: string
  twitter_verified: boolean
  verified: boolean
  tags: string[]
}

type TokenPriceCandle = {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

type TokenPriceHistoryResponse = TokenPriceCandle[]

const calculateAMMPriceChange = (
  data: TokenInfo,
  amountInSats: bigint,
  buy: boolean
): SwapProviderQuote => {
  if (!data.bonded) throw new Error('Only bonded tokens are supported.')
  if (amountInSats === 0n)
    return { price: 0, priceHuman: 0, slippage: 0, provider: SwapProvider.ODIN }

  const btcReserve = BigInt(data.btc_liquidity)
  const tokenReserve = BigInt(data.token_liquidity)
  const kInvariant = btcReserve * tokenReserve

  const priceScale = BigInt(10 ** (data.decimals + data.divisibility))

  const FEE_NUMERATOR = 995n
  const FEE_DENOMINATOR = 1000n

  let outputAmountBitcoin: bigint
  let tradePriceScaled: bigint
  let outputAmountHuman: number

  if (buy) {
    const amountAfterFee = (amountInSats * FEE_NUMERATOR) / FEE_DENOMINATOR
    const btcReserveAfter = btcReserve + amountAfterFee
    const tokenReserveAfter = kInvariant / btcReserveAfter
    const tokenOut = tokenReserve - tokenReserveAfter

    if (tokenOut <= 0n) return { price: 0, priceHuman: 0, slippage: 0, provider: SwapProvider.ODIN }

    tradePriceScaled = (amountAfterFee * priceScale) / tokenOut
    outputAmountBitcoin = tokenOut
    outputAmountHuman = Number(tokenOut) / 10 ** (data.decimals + data.divisibility)
  } else {
    const tokenReserveAfter = tokenReserve + amountInSats
    const btcReserveAfter = kInvariant / tokenReserveAfter
    const btcOut = btcReserve - btcReserveAfter

    if (btcOut <= 0n) return { price: 0, priceHuman: 0, slippage: 0, provider: SwapProvider.ODIN }

    tradePriceScaled = (((btcOut * FEE_NUMERATOR) / FEE_DENOMINATOR) * priceScale) / amountInSats
    outputAmountBitcoin = btcOut
    outputAmountHuman = Number(btcOut) / 1e11
  }

  const tradePriceNum = Number(tradePriceScaled)
  const referencePrice = data.price
  const slippage = buy
    ? ((tradePriceNum - referencePrice) / referencePrice) * 100
    : ((referencePrice - tradePriceNum) / referencePrice) * 100

  return {
    price: Number(outputAmountBitcoin),
    priceHuman: outputAmountHuman,
    slippage: Math.max(0, slippage),
    provider: SwapProvider.ODIN,
  }
}

class OdinClient {
  public agent: HttpAgent
  private backendHelperActor: ActorSubclass<OdinActor>
  private tradeHelperActor: ActorSubclass<OdinTradeActor>

  constructor(agent: HttpAgent) {
    this.agent = agent
    this.tradeHelperActor = createActor(odinTradeActor, {
      canisterId: ODIN_TRADE_CANISTER,
      agent,
    })

    this.backendHelperActor = createActor(odinActor, {
      canisterId: ODIN_BACKEND_CANISTER,
      agent,
    })
  }

  static async request(path: string) {
    const response = await fetch(`${ODIN_API_URL}/${path}`)

    if (response.ok) {
      return await response.json()
    } else {
      throw await response.text()
    }
  }

  async getQuote(
    amountInSats: bigint,
    fromToken: OdinToken,
    toToken: OdinToken
  ): Promise<SwapProviderQuote | SwapProviderQuoteFetchError> {
    if (fromToken.id !== ODINBTC_ID && toToken.id !== ODINBTC_ID) {
      return {
        error: 'Odin supports only swaps from/to Odin BTC',
        provider: SwapProvider.ODIN,
      }
    }

    try {
      const token = fromToken.id === ODINBTC_ID ? toToken : fromToken
      const response: TokenInfo = await OdinClient.request(`token/${token.id}?show_hidden=true`)

      if (response.bonded) {
        return calculateAMMPriceChange(
          response,
          amountInSats,
          fromToken.id === ODINBTC_ID ? true : false
        )
      } else {
        return { error: UNSUPPORTED_TOKEN, provider: SwapProvider.ODIN }
      }
    } catch (e) {
      return { error: UNKNOWN_ERROR, provider: SwapProvider.ODIN }
    }
  }

  async depositCkBTC(height: bigint) {
    return this.backendHelperActor.ckbtc_deposit([], height)
  }

  async transferToken(to: string, tokenId: string, amount: string) {
    return this.tradeHelperActor.token_transfer({
      to,
      tokenid: tokenId,
      amount: BigInt(amount),
    })
  }

  async withdrawToken(tokenId: string, to: string, amount: string) {
    return this.tradeHelperActor.token_withdraw({
      protocol: { ckbtc: null },
      tokenid: tokenId,
      address: to,
      amount: BigInt(amount),
    })
  }

  async swap(fromToken: OdinToken, toToken: OdinToken, amount: bigint): Promise<number> {
    const isBtcFrom = fromToken.id === ODINBTC_ID
    const tradeType: TradeType = isBtcFrom ? { buy: null } : { sell: null }
    const token = isBtcFrom ? toToken : fromToken
    const tradeAmount: TradeAmount = isBtcFrom
      ? { btc: amount } // buying tokens with BTC
      : { token: amount } // selling tokens for BTC

    const swapResponse = await this.tradeHelperActor.token_trade({
      tokenid: token.id,
      typeof: tradeType,
      settings: [],
      amount: tradeAmount,
    })

    if ('ok' in swapResponse) {
      try {
        const tokenData: TokenInfo = await OdinClient.request(`token/${token.id}?show_hidden=true`)

        return calculateAMMPriceChange(tokenData, amount, Object.keys(tradeType)[0] === 'buy').price
      } catch {
        return 0
      }
    }

    if ('err' in swapResponse) {
      throw new Error(swapResponse.err)
    }

    throw new Error('unknown error')
  }
}

export default OdinClient
